import AdCard from "@/components/ad-card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordian";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FacebookAd } from "@/hooks/api/use-brand-ads";
import React from "react";
import ReactMarkdown from "react-markdown";

// Add these type definitions at the top of the file, after the imports
interface MediaTypeItem {
  media_type: string;
  count: number;
}

interface DisplayFormatItem {
  display_format: string;
  count: number;
}

interface FormatAnalysis {
  format_trends: string;
  media_type_distribution: MediaTypeItem[];
  display_format_distribution: DisplayFormatItem[];
}

interface ContentAnalysis {
  common_hooks: string[];
  creative_elements: string[];
  recurring_keywords: string[];
  value_propositions: string[];
}

interface IndustryContext {
  industry_norms: string;
  unique_differentiators: string;
  competitive_positioning: string;
  alignment_with_industry_best_practices: string;
}

interface LongestRunningAd {
  ad_id: string;
  key_elements: string;
  duration_days: number;
}

interface AdVolumeData {
  frequency_pattern: string;
}

interface StructuredData {
  ad_volume?: AdVolumeData;
  key_insights?: string[];
  format_analysis?: FormatAnalysis;
  content_analysis?: ContentAnalysis;
  recommendations?: string[];
  industry_context?: IndustryContext;
  longest_running_ads?: LongestRunningAd[];
}

// Simple Ad Preview component for popovers
function SimpleAdPreview({ ad }: { ad: FacebookAd }) {
  const [cardIndex, setCardIndex] = React.useState(0);

  if (!ad.latest_snapshot) return null;

  const hasVideo =
    ad.latest_snapshot.cards[cardIndex]?.video_hd_url ||
    ad.latest_snapshot.video_hd_url ||
    ad.latest_snapshot.cards.some((card) => card.video_hd_url);

  const imageUrl =
    ad.latest_snapshot.cards[cardIndex]?.resized_image_url ||
    ad.latest_snapshot.resized_image_url ||
    ad.latest_snapshot.cards[cardIndex]?.video_preview_image_url ||
    ad.latest_snapshot.video_preview_image_url;

  const bodyText =
    ad.latest_snapshot.cards[cardIndex]?.body_text ||
    ad.latest_snapshot.body_text ||
    "";

  const hasMultipleCards = ad.latest_snapshot.cards.length > 1;

  return (
    <div className="flex flex-col">
      {bodyText && <p className="text-xs line-clamp-2 mb-2">{bodyText}</p>}

      <div className="relative">
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Ad preview"
            className="w-full max-h-[150px] object-contain"
          />
        )}
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-8 h-8 rounded-full bg-black/50 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polygon points="10 8 16 12 10 16 10 8"></polygon>
              </svg>
            </div>
          </div>
        )}
      </div>

      {hasMultipleCards && (
        <div className="mt-2 flex justify-center">
          <div className="flex items-center gap-1 p-0.5 bg-muted rounded-md overflow-x-auto max-w-full">
            {ad.latest_snapshot.cards.map((card, index) => (
              <div
                key={index}
                className={`w-8 h-8 cursor-pointer overflow-hidden rounded-sm border-2 ${
                  index === cardIndex ? "border-primary" : "border-transparent"
                }`}
                onClick={() => setCardIndex(index)}
              >
                <div className="relative w-full h-full">
                  <img
                    src={
                      card.video_preview_image_url ||
                      card.resized_image_url ||
                      ""
                    }
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {card.video_hd_url && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <polygon points="10 8 16 12 10 16 10 8"></polygon>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Ad ID Popover component
function AdIdPopover({ adId, ads }: { adId: string; ads: FacebookAd[] }) {
  const ad = ads.find((ad) => ad.external_ad_id === adId);

  if (!ad) return <span>{adId}</span>;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <span className="text-primary cursor-pointer underline underline-offset-4">
          {adId}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <SimpleAdPreview ad={ad} />
      </PopoverContent>
    </Popover>
  );
}

// Custom ReactMarkdown component to render ad IDs with popovers
function MarkdownWithAdIdPopovers({
  children,
  ads,
}: {
  children: string;
  ads: FacebookAd[];
}) {
  // Regular expression to match ad IDs in the format "Ad 1234567890123456"
  const adIdRegex = /Ad (\d{15,})/g;

  // Custom renderer for text nodes
  const components = {
    // Use the correct type for the text component
    p: ({ children, ...props }: React.HTMLProps<HTMLParagraphElement>) => {
      if (!children) return <p {...props}>{children}</p>;

      // Process each child node
      const processedChildren = React.Children.map(children, (child) => {
        if (typeof child !== "string") return child;

        const parts = [];
        let lastIndex = 0;
        let match;

        // Reset regex state
        adIdRegex.lastIndex = 0;

        // Find all ad ID matches in the text
        let matchResult;
        while ((matchResult = adIdRegex.exec(child)) !== null) {
          match = matchResult;
          // Add text before the match
          if (match.index > lastIndex) {
            parts.push(child.substring(lastIndex, match.index));
          }

          // Extract the ad ID
          const fullMatch = match[0]; // "Ad 1234567890123456"
          const adId = match[1]; // "1234567890123456"

          // Add the "Ad " prefix as regular text
          parts.push("Ad ");

          // Add the ad ID with popover
          parts.push(
            <AdIdPopover
              key={`ad-${adId}-${match.index}`}
              adId={adId}
              ads={ads}
            />
          );

          lastIndex = match.index + fullMatch.length;
        }

        // Add any remaining text
        if (lastIndex < child.length) {
          parts.push(child.substring(lastIndex));
        }

        return parts;
      });

      return <p {...props}>{processedChildren}</p>;
    },
  };

  return <ReactMarkdown components={components}>{children}</ReactMarkdown>;
}

interface AdStrategyProps {
  overview?: string;
  strategyEvolution?: string;
  createdAt?: string;
  ads: FacebookAd[];
  structuredData?: StructuredData;
}

export default function AdStrategy({
  overview,
  strategyEvolution,
  createdAt,
  ads,
  structuredData,
}: AdStrategyProps) {
  if (!overview && !strategyEvolution && !structuredData) return null;

  // Helper function to safely render markdown content
  const renderMarkdown = (content: string | undefined) => {
    if (!content) return null;
    return (
      <MarkdownWithAdIdPopovers ads={ads}>{content}</MarkdownWithAdIdPopovers>
    );
  };

  return (
    <div className="space-y-4">
      <Accordion type="multiple" className="w-full rounded-lg border">
        {(overview || structuredData) && (
          <AccordionItem value="strategy" className="border-b">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="flex items-center gap-2">
                <span className="hover:no-underline">
                  Current Strategy Analysis
                </span>
                {createdAt && (
                  <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-1 rounded-full no-underline">
                    Updated {new Date(createdAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4">
              {structuredData && Object.keys(structuredData).length > 0 ? (
                <div className="prose prose-sm dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:text-muted-foreground max-w-none py-4 space-y-6">
                  {/* Recommendations - now at the top */}
                  {structuredData?.recommendations && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">Recommendations</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {structuredData.recommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Key Insights */}
                  {structuredData?.key_insights && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">Key Insights</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {structuredData.key_insights.map((insight, index) => (
                          <li key={index}>{insight}</li>
                        ))}
                        {structuredData.format_analysis ? (
                          <li>
                            {structuredData.format_analysis.format_trends}
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  )}

                  {/* Recurring Keywords */}
                  {structuredData?.content_analysis?.recurring_keywords && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">
                        Recurring Keywords
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {structuredData.content_analysis.recurring_keywords.map(
                          (keyword, index) => (
                            <span
                              key={index}
                              className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                            >
                              {keyword}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Value Propositions */}
                  {structuredData?.content_analysis?.value_propositions && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">
                        Value Propositions
                      </h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {structuredData.content_analysis.value_propositions.map(
                          (prop, index) => (
                            <li key={index}>{prop}</li>
                          )
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Content Analysis - other parts besides keywords and value props */}
                  {structuredData?.content_analysis && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Content Analysis
                      </h3>

                      {structuredData.content_analysis.common_hooks && (
                        <div className="space-y-2">
                          <h4 className="text-base font-medium">
                            Common Hooks
                          </h4>
                          <ul className="list-disc pl-5 space-y-1">
                            {structuredData.content_analysis.common_hooks.map(
                              (hook, index) => (
                                <li key={index}>{hook}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}

                      {structuredData.content_analysis.creative_elements && (
                        <div className="space-y-2">
                          <h4 className="text-base font-medium">
                            Creative Elements
                          </h4>
                          <ul className="list-disc pl-5 space-y-1">
                            {structuredData.content_analysis.creative_elements.map(
                              (element, index) => (
                                <li key={index}>{element}</li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ad Volume and Frequency */}
                  {structuredData?.ad_volume && (
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold">Ad Volume</h3>
                      <p>{structuredData.ad_volume.frequency_pattern}</p>
                    </div>
                  )}

                  {/* Longest Running Ads */}
                  {structuredData?.longest_running_ads && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold">
                          Longest Running Ads
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          These ads have been running the longest, indicating
                          strong performance and ROI. Long-running ads typically
                          have effective hooks and messaging that resonates with
                          the target audience.
                        </p>
                      </div>
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 auto-rows-auto"
                        style={{ gridAutoRows: "min-content" }}
                      >
                        {structuredData.longest_running_ads.map(
                          (longestAd, index) => {
                            // Find the full ad data from the ads array
                            const adData = ads.find(
                              (ad) => ad.external_ad_id === longestAd.ad_id
                            );

                            if (!adData) {
                              // Fallback to just showing the ad ID and info if we can't find the full ad
                              return (
                                <div
                                  key={index}
                                  className="border rounded-lg p-2 space-y-1 text-xs"
                                >
                                  <div className="flex justify-between">
                                    <span className="text-primary">
                                      {longestAd.ad_id}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {longestAd.duration_days}d
                                    </span>
                                  </div>
                                  <p className="line-clamp-1">
                                    {longestAd.key_elements}
                                  </p>
                                </div>
                              );
                            }

                            // If we found the ad, render the AdCard component
                            return (
                              <div key={index}>
                                <AdCard
                                  ad={adData}
                                  brand={adData.brand}
                                  className="border-0 [&>div:last-child]:mb-0 [&_img]:mb-0"
                                />
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}

                  {/* Industry Context / Competitive Positioning */}
                  {structuredData?.industry_context && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Competitive Positioning
                      </h3>

                      {structuredData.industry_context
                        .competitive_positioning && (
                        <div className="space-y-2">
                          <p>
                            {
                              structuredData.industry_context
                                .competitive_positioning
                            }
                          </p>
                        </div>
                      )}

                      {structuredData.industry_context
                        .unique_differentiators && (
                        <div className="space-y-2">
                          <h4 className="text-base font-medium">
                            Unique Differentiators
                          </h4>
                          <p>
                            {
                              structuredData.industry_context
                                .unique_differentiators
                            }
                          </p>
                        </div>
                      )}

                      {structuredData.industry_context.industry_norms && (
                        <div className="space-y-2">
                          <h4 className="text-base font-medium">
                            Industry Norms
                          </h4>
                          <p>
                            {structuredData.industry_context.industry_norms}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="prose prose-sm dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:text-muted-foreground max-w-none py-4">
                  {renderMarkdown(overview)}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* {strategyEvolution && (
          <AccordionItem value="evolution" className="border-0">
            <AccordionTrigger className="px-4 hover:no-underline">
              <div className="items-center gap-2">
                <div className="hover:no-underline">Strategy Evolution</div>
                <div className="text-xs font-normal text-muted-foreground no-underline">
                  See how this strategy has evolved over time
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4">
              <div className="prose prose-sm dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:text-muted-foreground max-w-none py-4">
                {renderMarkdown(strategyEvolution)}
              </div>
            </AccordionContent>
          </AccordionItem>
        )} */}
      </Accordion>
    </div>
  );
}
